import { zodResolver } from '@hookform/resolvers/zod'
import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { profileService } from '../api/services/profile.service'
import { AppShell } from '../components/layout/AppShell'
import { Button } from '../components/ui/button'
import { FieldLabel, FormRequiredLegend } from '../components/ui/field-label'
import { Input } from '../components/ui/input'
import { useAuth } from '../context/AuthContext'
import { SALUTATION_OPTIONS, type ProfileUser } from '../types/profile'

const profileSchema = z.object({
  salutation: z.string().min(1, 'Salutation is required'),
  first_name: z.string().min(1, 'First name is required'),
  surname: z.string().min(1, 'Surname is required'),
  phone_number: z.string().optional(),
})

type ProfileFormValues = z.infer<typeof profileSchema>

export function ProfilePage() {
  const { session, setSession } = useAuth()
  const [profile, setProfile] = useState<ProfileUser | null>(null)
  const [loading, setLoading] = useState(true)
  const [selectedImage, setSelectedImage] = useState<File | null>(null)
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  const [removeImage, setRemoveImage] = useState(false)

  const form = useForm<ProfileFormValues>({
    resolver: zodResolver(profileSchema),
    defaultValues: {
      salutation: 'mr',
      first_name: '',
      surname: '',
      phone_number: '',
    },
  })

  useEffect(() => {
    let cancelled = false

    const loadProfile = async () => {
      setLoading(true)

      try {
        const response = await profileService.getProfile()

        if (cancelled) {
          return
        }

        setProfile(response.data)
        form.reset({
          salutation: response.data.salutation,
          first_name: response.data.first_name,
          surname: response.data.surname,
          phone_number: response.data.phone_number ?? '',
        })
        setPreviewUrl(response.data.image)
      } catch {
        // API errors are toasted globally by the axios interceptor.
      } finally {
        if (!cancelled) {
          setLoading(false)
        }
      }
    }

    void loadProfile()

    return () => {
      cancelled = true
    }
  }, [form])

  const onImageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] ?? null
    setSelectedImage(file)
    setRemoveImage(false)

    if (file) {
      setPreviewUrl(URL.createObjectURL(file))
    }
  }

  const onRemoveImage = () => {
    setSelectedImage(null)
    setRemoveImage(true)
    setPreviewUrl(null)
  }

  const onSubmit = form.handleSubmit(async (values) => {
    try {
      const response = await profileService.updateProfile({
        ...values,
        phone_number: values.phone_number || null,
        image: selectedImage,
        remove_image: removeImage,
      })

      setProfile(response.data)
      setPreviewUrl(response.data.image)
      setSelectedImage(null)
      setRemoveImage(false)

      if (session) {
        setSession({
          ...session,
          user: {
            ...session.user,
            salutation: response.data.salutation,
              first_name: response.data.first_name,
            surname: response.data.surname,
            phone_number: response.data.phone_number,
            image: response.data.image,
          },
        })
      }

    } catch {
      // API errors are toasted globally by the axios interceptor.
    }
  })

  return (
    <AppShell>
      <section className="rounded-lg border border-parch-100 bg-white p-6 shadow-card">
        <h1 className="font-display text-2xl font-bold">Profile</h1>
        <p className="mt-1 text-sm text-ink-600">
          Update your personal details and profile photo. Email and role cannot be changed here.
        </p>

        {loading && <p className="mt-6 text-sm text-ink-600">Loading profile...</p>}

        {!loading && profile && (
          <form className="mt-6 space-y-6" onSubmit={onSubmit}>
            <FormRequiredLegend />
            <div className="flex flex-wrap items-center gap-4">
              <div className="grid h-20 w-20 place-items-center overflow-hidden rounded-full bg-green-100 text-lg font-semibold text-green-700">
                {previewUrl ? (
                  <img src={previewUrl} alt="Profile" className="h-full w-full object-cover" />
                ) : (
                  `${profile.first_name.charAt(0)}${profile.surname.charAt(0)}`.toUpperCase()
                )}
              </div>
              <div className="space-y-2">
                <FieldLabel optional htmlFor="profile-image">
                  Profile photo
                </FieldLabel>
                <div className="flex flex-wrap gap-2">
                  <Input id="profile-image" type="file" accept="image/png,image/jpeg" onChange={onImageChange} />
                  {(previewUrl || profile.image) && (
                    <Button type="button" variant="secondary" size="sm" onClick={onRemoveImage}>
                      Remove Photo
                    </Button>
                  )}
                </div>
              </div>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <FieldLabel required htmlFor="salutation">
                  Salutation
                </FieldLabel>
                <select
                  id="salutation"
                  className="w-full rounded-md border border-parch-100 bg-white px-3 py-2 text-sm outline-none focus:border-green-600 focus:ring-2 focus:ring-green-600/20"
                  {...form.register('salutation')}
                >
                  {SALUTATION_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="space-y-2">
                <FieldLabel required htmlFor="first_name">
                  First name
                </FieldLabel>
                <Input id="first_name" {...form.register('first_name')} />
              </div>

              <div className="space-y-2">
                <FieldLabel required htmlFor="surname">
                  Surname
                </FieldLabel>
                <Input id="surname" {...form.register('surname')} />
              </div>

              <div className="space-y-2">
                <FieldLabel optional htmlFor="phone_number">
                  Contact info
                </FieldLabel>
                <Input id="phone_number" {...form.register('phone_number')} />
              </div>

              <div className="space-y-2">
                <FieldLabel htmlFor="email">Email</FieldLabel>
                <Input id="email" value={profile.email} readOnly disabled />
              </div>
            </div>

            <div className="flex justify-end">
              <Button type="submit" disabled={form.formState.isSubmitting}>
                {form.formState.isSubmitting ? 'Saving...' : 'Save Profile'}
              </Button>
            </div>
          </form>
        )}
      </section>
    </AppShell>
  )
}
